import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import axios from 'axios'
// FIRST, and before anything that could send a request: it sets the base every API
// call is written relative to (see apiBase). A demo build whose first request went
// out unprefixed would be the demo asking the real app for its answer.
import './apiBase'
import { LanguageProvider } from './contexts/LanguageContext'
import { NoticeApp } from './apps/notice/NoticeApp'
import { watchImageErrors } from './apps/notice/retryImages'
import { serverLog, watchPage } from './apps/notice/serverLog'
import { isPublicTutorialRoute } from './publicRoute'
import './index.css'

// NO THEME CLASS ON <html>, EVER. The app has one look — the blueprint page and
// its white cards, see index.css — so there is nothing to apply before paint and
// nothing to persist. `dark:` variants work only where a subtree carries the class
// itself: the sidebar rail is the only one (AppSidebar), which is why its interior
// stays dark over the blue page. So they are written only in what renders there —
// LangToggle, ProfileMenu, TabbedSidebarLayout, RunNav, CenteredSelect,
// BlenderAddonModal — and anywhere else one is dead code.

// NoticeIA cookies (notice_access_token / notice_refresh_token) ride along on
// every request.
axios.defaults.withCredentials = true

// A picture whose one request failed is asked for again — see retryImages. Outside React
// on purpose: it is about the DOM rather than about any component, it has to catch images
// mounted by parts of the app that know nothing about it, and there is nothing to tear
// down in a page that lives as long as the tab.
watchImageErrors()

// THE APP INSTALLABLE ON A PHONE (see index.html): its manifest, everywhere but the published
// tutorial, which a visitor saving it to their home screen wants to reopen, not the app's front page.
if (!isPublicTutorialRoute()) {
  const manifest = document.createElement('link')
  manifest.rel = 'manifest'
  manifest.href = `${import.meta.env.BASE_URL}manifest.webmanifest`
  document.head.appendChild(manifest)
  // The app's own page log, the tutorial keeping its own (see PublicTutorial): for as long as the
  // tab lives, so nothing to tear down.
  watchPage(serverLog('load', 'page-app'))
}

// The app is served under a base path (Vite `base` -> import.meta.env.BASE_URL,
// e.g. "/app/"). The router's basename is derived from it, so internal links are
// written root-relative ("/home") and resolve under the base automatically —
// moving the app to a different path (or a domain root) is just a build-time
// base change, no code edits.
const basename = import.meta.env.BASE_URL.replace(/\/+$/, '') || '/'

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LanguageProvider>
      <BrowserRouter basename={basename}>
        <NoticeApp />
      </BrowserRouter>
    </LanguageProvider>
  </StrictMode>
)
